import { isArray } from 'lodash';
import type { IntlShape } from 'react-intl';

import { wordCount } from '../../utils/wordCount';
import { Field } from './types';

export const requiredValidator =
  (requiredFields: string[], intl: IntlShape) =>
  (field: Field, value: unknown) => {
    const isValueValid = isArray(value) ? value.length > 0 : !!value;

    const requiredFieldLabel = intl.formatMessage({
      id: 'Validators.requiredValidator.requiredField',
      defaultMessage: 'This field is required',
      description: 'Error message for required field validation',
    });
    if (requiredFields.includes(field.name)) {
      switch (typeof value) {
        case 'string':
          return value.trim().length > 0 ? null : requiredFieldLabel;
        case 'number':
          return value === 0 || value ? null : requiredFieldLabel;
        default:
          return isValueValid ? null : requiredFieldLabel;
      }
    }
    return null;
  };

export const uuidValidator =
  (fieldNames: string[], intl: IntlShape) => (field: Field, value: unknown) => {
    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (
      fieldNames.includes(field.name) &&
      value &&
      !uuidPattern.test(value as string)
    ) {
      return intl.formatMessage({
        id: 'Validators.uuidValidator',
        defaultMessage:
          'The value is not a valid UUID, use pattern 00000000-0000-0000-0000-000000000000',
        description: 'Error message for invalid UUID format',
      });
    }
    return null;
  };

export const lengthValidator =
  (fieldNames: string[], length: number, intl: IntlShape) =>
  (field: Field, value: unknown) => {
    if (
      fieldNames.includes(field.name) &&
      typeof value === 'string' &&
      value.length > length
    ) {
      return intl.formatMessage(
        {
          id: 'Validators.lengthValidator',
          defaultMessage: 'This field must be at most {length} characters long',
          description:
            'Error message for field length validation. Example: length = "100"',
        },
        {
          length,
        },
      );
    }
    return null;
  };

export const wordLengthValidator =
  (fieldNames: string[], length: number, intl: IntlShape) =>
  (field: Field, value: unknown) => {
    if (
      fieldNames.includes(field.name) &&
      typeof value === 'string' &&
      wordCount(value) > length
    ) {
      return intl.formatMessage(
        {
          id: 'Validators.wordLengthValidator',
          defaultMessage: 'This field must be at most {length} words long',
          description:
            'Error message for field word count validation. Example: length = "50"',
        },
        {
          length,
        },
      );
    }
    return null;
  };
